import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tooltip } from 'antd';
import { STAGE_CONFIG, FlowNodeData, PractitionerOption, PractitionerTaskType } from '../../../../../types/healthcare/TreatmentPlanTypes';
import { getStageNodeIcon } from '../../../../../utils/healthcare/treatmentPlanIcons';
import SectionGroupNode from './SectionGroupNode';

interface StageNodeProps extends NodeProps {
  data: FlowNodeData & { selected?: boolean };
  selected: boolean;
}

const BaseNode: React.FC<{
  data: FlowNodeData;
  selected?: boolean;
  sourceHandles?: { id: string; label: string; style?: React.CSSProperties }[];
  hasTarget?: boolean;
}> = ({ data, selected, sourceHandles, hasTarget = true }) => {
  const cfg = STAGE_CONFIG[data.stage_type];
  const color = cfg?.color ?? '#6b7280';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Stage number badge — lives outside overflow:hidden so it's never clipped */}
      {data.stage_number != null && (
        <div
          style={{
            position: 'absolute',
            top: -9,
            right: -9,
            background: '#fff',
            border: `1.5px solid ${color}`,
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 700,
            color,
            zIndex: 10,
            lineHeight: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}
        >
          {data.stage_number}
        </div>
      )}
      {data.is_loop_target && (
        <div
          style={{
            position: 'absolute',
            top: -9,
            right: -31,
            background: '#ede9fe',
            border: '1.5px solid #8b5cf6',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: '#8b5cf6',
            zIndex: 10,
            lineHeight: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}
        >
          ↺
        </div>
      )}
    <div
      style={{
        minWidth: 180,
        maxWidth: 220,
        borderRadius: 10,
        border: `2px solid ${selected ? 'var(--mg-primary)' : color}`,
        background: 'var(--mg-bg-elevated)',
        boxShadow: selected
          ? `0 0 0 3px rgba(22, 119, 255, 0.18), 0 4px 16px rgba(0,0,0,0.12)`
          : '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        cursor: 'default',
        transition: 'box-shadow 0.15s',
      }}
    >

      {/* Header */}
      <div
        style={{
          background: color,
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {getStageNodeIcon(data.stage_type, data.configuration?.task_type as PractitionerTaskType | undefined)}
        <span>{cfg?.label}</span>
        {data.is_entry_point && (
          <span
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,0.3)',
              borderRadius: 4,
              padding: '1px 5px',
              fontSize: 9,
            }}
          >
            ENTRY
          </span>
        )}
        {data.is_required === false && (
          <span
            style={{
              marginLeft: data.is_entry_point ? 4 : 'auto',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: 4,
              padding: '1px 5px',
              fontSize: 9,
            }}
          >
            OPT
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--mg-text)', marginBottom: 2, wordBreak: 'break-word' }}>
          {data.label || 'Untitled'}
        </div>
        {data.description && (
          <div style={{ fontSize: 11, color: 'var(--mg-text-secondary)', wordBreak: 'break-word' }}>{data.description}</div>
        )}
      </div>

      {/* Target handle (top) */}
      {hasTarget && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: 'var(--mg-border-strong)', width: 10, height: 10, border: '2px solid var(--mg-bg-elevated)' }}
        />
      )}

      {/* Source handles (bottom) */}
      {sourceHandles ? (
        sourceHandles.map((h) => (
          <Tooltip key={h.id} title={h.label} placement="bottom">
            <Handle
              type="source"
              position={Position.Bottom}
              id={h.id}
              style={{
                background: color,
                width: 10,
                height: 10,
                border: '2px solid var(--mg-bg-elevated)',
                ...(h.style ?? {}),
              }}
            />
          </Tooltip>
        ))
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: color, width: 10, height: 10, border: '2px solid var(--mg-bg-elevated)' }}
        />
      )}
    </div>
    </div>
  );
};

// ── Trigger ───────────────────────────────────────────────────────────────
export const TriggerNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} hasTarget={false} />
));
TriggerNode.displayName = 'TriggerNode';

// ── Action ────────────────────────────────────────────────────────────────
export const ActionNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
ActionNode.displayName = 'ActionNode';

// ── Condition ─────────────────────────────────────────────────────────────
export const ConditionNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode
    data={data}
    selected={selected}
    sourceHandles={[
      { id: 'condition_yes', label: 'Yes', style: { left: '30%' } },
      { id: 'condition_no', label: 'No', style: { left: '70%' } },
    ]}
  />
));
ConditionNode.displayName = 'ConditionNode';

// ── Time Condition ────────────────────────────────────────────────────────
export const TimeConditionNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
TimeConditionNode.displayName = 'TimeConditionNode';

// ── Watcher ───────────────────────────────────────────────────────────────
export const WatcherNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
WatcherNode.displayName = 'WatcherNode';

// ── Manual ────────────────────────────────────────────────────────────────
export const ManualNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
ManualNode.displayName = 'ManualNode';

// ── Sub-Plan Selection ────────────────────────────────────────────────────
export const SubPlanSelectionNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
SubPlanSelectionNode.displayName = 'SubPlanSelectionNode';

// ── Appointment ───────────────────────────────────────────────────────────
export const AppointmentNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
AppointmentNode.displayName = 'AppointmentNode';

// ── Practitioner Task ─────────────────────────────────────────────────────
export const PractitionerTaskNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
PractitionerTaskNode.displayName = 'PractitionerTaskNode';

// ── Patient Analysis ──────────────────────────────────────────────────────
export const PatientAnalysisNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
PatientAnalysisNode.displayName = 'PatientAnalysisNode';

// ── Practitioner Option ───────────────────────────────────────────────────
export const PractitionerOptionNode = memo(({ data, selected }: StageNodeProps) => {
  const options: PractitionerOption[] = data.configuration?.options ?? [];
  const count = options.length;

  const sourceHandles = count >= 2
    ? options.map((opt, i) => ({
        id: `option_${opt.option_key}`,
        label: opt.label || opt.option_key,
        style: { left: `${((i + 1) / (count + 1)) * 100}%` },
      }))
    : [
        { id: 'option_a', label: 'Option A', style: { left: '33%' } },
        { id: 'option_b', label: 'Option B', style: { left: '67%' } },
      ];

  return <BaseNode data={data} selected={selected} sourceHandles={sourceHandles} />;
});
PractitionerOptionNode.displayName = 'PractitionerOptionNode';

// ── Comprehensive Analysis ────────────────────────────────────────────────
export const ComprehensiveAnalysisNode = memo(({ data, selected }: StageNodeProps) => (
  <BaseNode data={data} selected={selected} />
));
ComprehensiveAnalysisNode.displayName = 'ComprehensiveAnalysisNode';

// ── Node type map for ReactFlow ───────────────────────────────────────────
export const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  time_condition: TimeConditionNode,
  watcher: WatcherNode,
  manual: ManualNode,
  sub_plan_selection: SubPlanSelectionNode,
  appointment: AppointmentNode,
  practitioner_task: PractitionerTaskNode,
  patient_analysis: PatientAnalysisNode,
  practitioner_option: PractitionerOptionNode,
  comprehensive_analysis: ComprehensiveAnalysisNode,
  section_group: SectionGroupNode,
};
